import "./Landing.css";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackPixelEvent } from "helpers/facebookPixel";

import dashboardImg from "assets/images/landing/product-dashboard-1.png";
import formulacaoImg from "assets/images/landing/product-formulacao.png";
import estoqueImg from "assets/images/landing/product-estoque.png";
import cobrancaImg from "assets/images/landing/product-cobranca.png";

const LogoMark = ({ size = 28, color = "var(--mint)" }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-label="Avant">
    <path d="M8 38 L20 18 L32 38 L24 38 L20 30 L16 38 Z" fill={color} />
    <path d="M32 26 L44 46 L56 26 L48 26 L44 36 L40 26 Z" fill={color} />
  </svg>
);

const TICKER_ITEMS = [
  ["PDV", "venda com carrinho e descontos"],
  ["LOTE", "rastreio com validade e FEFO"],
  ["BOM", "formulação multinível"],
  ["PRODUÇÃO", "lotes de saída com custo"],
  ["NFe", "emissão e contingência"],
  ["SPED", "exportação fiscal"],
  ["WHATSAPP", "régua de cobrança"],
  ["DASHBOARD", "vendas · estoque · produção"],
  ["AUDIT", "feature flags + histórico"],
  ["MODULAR", "ativa só o que precisa"],
];

const PAINS = [
  {
    fromTag: "Hoje",
    fromTitle: "ERP local que cai quando o servidor cai.",
    fromBody:
      "Roda numa máquina ou rede local. Se o servidor para, o caixa para. Backup, atualização e crash dependem da infra do local.",
    toTag: "Com Avant",
    toTitle: (
      <>
        Web pelo <span className="accent">navegador.</span>
      </>
    ),
    toBody:
      "Dados centralizados, acesso de qualquer lugar, sem instalação local. Servidor caiu? Você não dependia dele.",
  },
  {
    fromTag: "Hoje",
    fromTitle: "Sistema legado que trava no meio da venda.",
    fromBody:
      "Operador esperando tela carregar. Framework antigo, melhoria difícil, integração fiscal quebradiça.",
    toTag: "Com Avant",
    toTitle: (
      <>
        Stack moderna,
        <br />
        <span className="accent">fluxos limpos.</span>
      </>
    ),
    toBody:
      "Produto em evolução ativa, módulos pensados pra operação real, sem peso de legado por baixo.",
  },
  {
    fromTag: "Hoje",
    fromTitle: "Suporte que demora dias pra responder.",
    fromBody:
      "Bug simples vira risco operacional. Fiscal, estoque ou cobrança travados — e você sem poder mexer no sistema.",
    toTag: "Com Avant",
    toTitle: (
      <>
        Dono do produto <span className="accent">por perto.</span>
      </>
    ),
    toBody:
      "Iteração rápida e módulos adaptáveis por empresa. Você não tá preso num setup rígido feito pra todo mundo.",
  },
  {
    fromTag: "Hoje",
    fromTitle: "Cliente no caderno. Cobrança no zap manual.",
    fromBody:
      "Receivable esquecido, cobrança feita tarde, estoque no chute. Sem visão do que vendeu, do que deve, do que tá baixo.",
    toTag: "Com Avant",
    toTitle: (
      <>
        Clientes, AR e <span className="accent">cobrança</span> num só lugar.
      </>
    ),
    toBody:
      "CRM enxuto, contas a receber, dashboard de vendas e régua automática de WhatsApp — operando no mesmo banco de dados.",
  },
  {
    fromTag: "Hoje",
    fromTitle: "Produção descolada do estoque.",
    fromBody:
      "BOM em planilha, consumo manual, custo de lote sem rastreio. Saída e entrada não batem — e ninguém sabe o porquê.",
    toTag: "Com Avant",
    toTitle: (
      <>
        Fórmula, lote e <span className="accent">custo conectados.</span>
      </>
    ),
    toBody:
      "Indústria com BOM, FEFO, lote de saída com custo calculado, desvio e cancelamento. Da fórmula ao acabado, rastreável.",
  },
];

const MODGROUPS = [
  {
    code: "MOD.01",
    tier: "Essencial",
    title: (
      <>
        Núcleo de <span className="accent">operação.</span>
      </>
    ),
    pitch: "Base do sistema para vender, cadastrar e acompanhar o dia a dia.",
    items: [
      "PDV completo",
      "Importação por NF-e + CSV",
      "Clientes & fornecedores",
      "Vendedores · regiões",
      "Contas a receber",
      "Histórico de vendas",
    ],
  },
  {
    code: "MOD.02",
    tier: "Crescimento",
    title: (
      <>
        Financeiro & <span className="accent">cobrança.</span>
      </>
    ),
    pitch: "Pra quando o operacional já roda e a régua precisa apertar.",
    items: [
      "Cobrança automática",
      "WhatsApp & e-mail",
      "Bancos & maquininhas",
      "Plano de contas",
      "Condições de pagamento",
      "Cotações & limite de crédito",
    ],
  },
  {
    code: "MOD.03",
    tier: "Estoque",
    title: (
      <>
        Estoque por <span className="accent">lote.</span>
      </>
    ),
    pitch: "Quantidade não chega. Avant rastreia o ciclo inteiro.",
    items: [
      "Lotes & validade",
      "FEFO automático",
      "Movimentos & ajustes",
      "Transferências entre lojas",
      "Estoque mínimo & alerta",
      "Histórico por lote",
    ],
  },
  {
    code: "MOD.04",
    tier: "Indústria",
    title: (
      <>
        BOM, fórmula e <span className="accent">produção.</span>
      </>
    ),
    pitch: "Transforma fórmula em lote de saída com custo conectado.",
    items: [
      "Formulação multinível",
      "Lotes de produção",
      "Auto-fracionamento",
      "Consumo de matéria-prima",
      "Desvio & cancelamento",
      "Relatórios de produção",
    ],
  },
  {
    code: "MOD.05",
    tier: "Fiscal",
    title: (
      <>
        NFe & <span className="accent">SPED.</span>
      </>
    ),
    pitch: "Fiscal dentro da operação — não num sistema à parte.",
    items: [
      "Certificado A1 criptografado",
      "Naturezas & CFOP",
      "NFe, CCe & devolução",
      "DANFE em PDF",
      "Contingência automática",
      "Sintegra · SPED",
    ],
  },
  {
    code: "MOD.06",
    tier: "Extensões",
    title: (
      <>
        Operação <span className="accent">avançada.</span>
      </>
    ),
    pitch: "Add-ons que aceleram o dia a dia conforme a operação amadurece.",
    items: [
      "Leitor de código de barras",
      "Cotações & limite de crédito",
      "Plano de contas hierárquico",
      "Condições de pagamento AP",
      "Auditoria operacional",
      "Logística & CRM (em breve)",
    ],
  },
];

const PLANS = [
  {
    code: "P.01",
    key: "starter",
    name: "Starter",
    tagline: "Pra começar a operar sem caos.",
    monthly: "R$ 449",
    yearly: "R$ 4.490",
    bullets: [
      "Produtos, clientes, fornecedores",
      "Estoque por lote + FEFO",
      "Vendas, compras e movimentações",
      "Leitor de código de barras",
      "Usuários ilimitados",
    ],
    cta: "Começar teste grátis",
  },
  {
    code: "P.02",
    key: "pro",
    name: "Pro",
    tagline: "Fiscal completo: NF-e, SPED, vendas avançadas e CRM.",
    monthly: "R$ 999",
    yearly: "R$ 9.990",
    bullets: [
      "Tudo do Starter",
      "Emissão de NF-e e NFC-e",
      "SPED Fiscal e Contribuições",
      "Vendas avançadas + CRM",
      "Suporte fiscal prioritário",
    ],
    cta: "Começar teste grátis",
    recommended: true,
  },
  {
    code: "P.03",
    key: "enterprise",
    name: "Enterprise",
    tagline: "Tudo do Pro + Financial Pro incluso. Contrato sob medida.",
    monthly: "A partir de R$ 2.499",
    yearly: "A partir de R$ 24.990",
    bullets: [
      "Tudo do Pro",
      "Financial Pro incluso (boleto + PIX)",
      "Cobrança automática + auditoria",
      "Entregas + logística + CRM",
      "SLA dedicado, contrato sob medida",
    ],
    cta: "Falar com vendas",
    contact: true,
  },
];

const PLAN_ADDONS = [
  {
    code: "A.01",
    key: "industry",
    name: "Indústria",
    price: "R$ 299",
    yearly: "ou R$ 2.990/ano",
    pitch:
      "Ordens de produção, BOM multinível, custo industrial e chão de fábrica. Empilha em qualquer plano.",
  },
  {
    code: "A.02",
    key: "financial_pro",
    name: "Financial Pro",
    price: "R$ 129,90",
    yearly: "Incluso no Enterprise",
    pitch:
      "Boleto, PIX e conta Asaas integrada. 50 boletos inclusos por mês, R$ 2,49 por boleto extra.",
  },
];

const PERSONAS = [
  {
    n: "01",
    title: (
      <>
        Lojas e <span style={{ color: "var(--mint)" }}>distribuidores</span> de
        bairro.
      </>
    ),
    desc:
      "PDV, clientes, produtos e contas a receber descomplicados. Direto do navegador, sem instalação, sem firula.",
    stack: ["Núcleo on", "Estoque on", "Fiscal opt", "Cobrança opt"],
  },
  {
    n: "02",
    title: (
      <>
        Empresas com{" "}
        <span style={{ color: "var(--mint)" }}>estoque rastreado.</span>
      </>
    ),
    desc:
      "Quem trabalha com validade, transferência entre lojas, ajustes e auditoria de movimento. Lote, FEFO e custo no DNA.",
    stack: ["Núcleo on", "Estoque on", "Audit on"],
  },
  {
    n: "03",
    title: (
      <>
        <span style={{ color: "var(--mint)" }}>Indústrias</span> que produzem
        por fórmula.
      </>
    ),
    desc:
      "Tinta, química, alimento, ração, cosmético. Fórmula vira lote de saída, com consumo, desvio e custo registrados.",
    stack: ["Indústria on", "Estoque on", "Fiscal on"],
  },
  {
    n: "04",
    title: (
      <>
        Empresas que querem{" "}
        <span style={{ color: "var(--mint)" }}>evoluir o fiscal.</span>
      </>
    ),
    desc:
      "Precisam de NFe hoje, Sintegra e SPED amanhã — sem separar operação de fiscal em sistemas diferentes.",
    stack: ["Fiscal on", "SPED add", "Audit on"],
  },
];

const STEPS = [
  {
    n: "01",
    tagA: "PASSO 01",
    tagB: "ENTRA NA PLATAFORMA",
    h: (
      <>
        Cria a empresa
        <br />e ativa o <span style={{ color: "var(--mint)" }}>essencial.</span>
      </>
    ),
    p: "Núcleo, cadastros e contas a receber. Sem instalação, pelo navegador. Você já vende no PDV no mesmo dia.",
    punch: (
      <>
        Operação <span className="mint">no ar</span> em horas.
      </>
    ),
  },
  {
    n: "02",
    tagA: "PASSO 02",
    tagB: "ATIVE OS MÓDULOS",
    h: (
      <>
        Liga o que a operação{" "}
        <span style={{ color: "var(--mint)" }}>pedir.</span>
      </>
    ),
    p: "Estoque por lote quando precisar rastrear validade. Indústria quando começar a produzir. Fiscal quando emitir nota. Cada módulo conversa com os outros.",
    punch: (
      <>
        Cresce <span className="mint">sem trocar de sistema.</span>
      </>
    ),
  },
  {
    n: "03",
    tagA: "PASSO 03",
    tagB: "ROTINA NO PILOTO",
    h: (
      <>
        Cobrança e relatórios{" "}
        <span style={{ color: "var(--mint)" }}>no automático.</span>
      </>
    ),
    p: "Régua de WhatsApp dispara nas parcelas em atraso. Dashboard mostra vendas, produção e fluxo de caixa em tempo real. Auditoria registra cada mudança.",
    punch: (
      <>
        Você cuida do negócio. <span className="mint">A gente da rotina.</span>
      </>
    ),
  },
];

const MarqueeRow = ({ children, reverse }) => (
  <div className="marquee-row">
    <div
      className="marquee-track"
      style={reverse ? { animationDirection: "reverse" } : undefined}
    >
      {[0, 1].map((k) => (
        <span key={k} className="item">
          {children}
        </span>
      ))}
    </div>
  </div>
);

const HeroMarquee = ({ goSignup }) => (
  <section className="hero marquee">
    <MarqueeRow>
      <span>PDV</span>
      <span className="mdot" />
      <span className="outline">ESTOQUE</span>
      <span className="mdot" />
      <span className="mint italic">PRODUÇÃO</span>
      <span className="mdot" />
      <span>NFe</span>
      <span className="mdot" />
      <span className="outline">FÓRMULA</span>
      <span className="mdot" />
    </MarqueeRow>
    <MarqueeRow reverse>
      <span className="mint">LOTE</span>
      <span className="mdot" />
      <span className="outline">FEFO</span>
      <span className="mdot" />
      <span className="italic">WHATSAPP</span>
      <span className="mdot" />
      <span className="outline">SPED</span>
      <span className="mdot" />
      <span>SINTEGRA</span>
      <span className="mdot" />
    </MarqueeRow>
    <div className="body">
      <div>
        <h1>
          ERP <span className="accent">modular</span>
          <br />
          para empresas brasileiras.
        </h1>
        <p className="lede">
          PDV, estoque por lote, financeiro, produção, cobrança por WhatsApp e
          fiscal numa plataforma web.{" "}
          <strong>Cada empresa ativa só os módulos que precisa</strong> — e
          cresce de etapa em etapa, sem trocar de sistema.
        </p>
        <div className="cta-row">
          <button type="button" className="btn primary" onClick={goSignup}>
            Começar grátis <span className="btn-arrow">→</span>
          </button>
          <a href="#modulos" className="btn ghost">
            Ver módulos
          </a>
        </div>
      </div>
      <div className="meta">
        <div>
          <strong>NÚCLEO</strong>PDV · cadastros · financeiro
        </div>
        <div>
          <strong>ESTOQUE</strong>lote · FEFO · transferência
        </div>
        <div>
          <strong>INDÚSTRIA</strong>BOM · OP · consumo
        </div>
        <div>
          <strong>FISCAL</strong>NFe · SPED · Sintegra
        </div>
      </div>
    </div>
  </section>
);

const Landing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackPixelEvent("ViewContent", {
      content_name: "Landing Page",
      content_category: "Marketing",
      content_type: "website",
    });
  }, []);

  const goSignup = () => navigate("/signup");
  const goLogin = () => navigate("/login");

  return (
    <div className="landing-page" id="top">
      <nav className="nav">
        <a href="#top" className="nav-brand">
          <span className="nav-brand-mark">
            <LogoMark size={26} />
          </span>
          <span>Avant / ERP</span>
        </a>
        <div className="nav-links">
          <a href="#modulos">Módulos</a>
          <a href="#precos">Preços</a>
          <a href="#industria">Indústria</a>
          <a href="#fiscal">Fiscal</a>
          <a href="#para-quem">Para quem é</a>
        </div>
        <div className="nav-cta">
          <button type="button" className="btn ghost" onClick={goLogin}>
            Entrar
          </button>
          <button type="button" className="btn primary" onClick={goSignup}>
            Começar grátis <span className="btn-arrow">→</span>
          </button>
        </div>
      </nav>

      <HeroMarquee goSignup={goSignup} />

      <div className="ticker" aria-hidden="true">
        <div className="ticker-track">
          {[0, 1].map((k) => (
            <div key={k} style={{ display: "flex", gap: 40 }}>
              {TICKER_ITEMS.map(([a, b], i) => (
                <span key={`${k}-${i}`} className="pill">
                  <span className="mark">▸</span>
                  <strong>{a}</strong>
                  <span>— {b}</span>
                  <span className="sep">···</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <section className="section" id="pains">
        <div className="section-head">
          <span className="kicker">02 / De · Para</span>
          <h2>
            Cinco dores comuns.
            <br />
            <span className="accent">Cinco respostas diretas.</span>
          </h2>
        </div>
        <div className="fromto">
          <div className="fromto-head">
            <span>#</span>
            <span className="lbl-from">DE · onde você está</span>
            <span className="lbl-to">
              <span className="sq" />
              PARA · onde Avant te leva
            </span>
          </div>
          {PAINS.map((p, i) => (
            <div key={i} className="fromto-row">
              <div className="fromto-num">{String(i + 1).padStart(2, "0")}</div>
              <div className="fromto-from">
                <span className="tag">{p.fromTag}</span>
                <div className="title">{p.fromTitle}</div>
                <p>{p.fromBody}</p>
              </div>
              <div className="fromto-to">
                <span className="tag">{p.toTag}</span>
                <div className="title">{p.toTitle}</div>
                <p>{p.toBody}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="snapshot">
        <div className="snapshot-head">
          <div>
            <div className="kicker">03 / Plataforma</div>
            <h2>
              O sistema é a <span className="accent">prova.</span>
            </h2>
          </div>
          <div>
            <p className="lede">
              Painel de vendas, estoque por lote, produção e fiscal — todos no
              mesmo lugar, todos respondendo aos{" "}
              <strong>módulos ativados</strong> para a empresa.
            </p>
            <div className="badge-row">
              <span className="badge on">
                <span className="sq" />
                Núcleo
              </span>
              <span className="badge on">
                <span className="sq" />
                Estoque
              </span>
              <span className="badge on">
                <span className="sq" />
                Indústria
              </span>
              <span className="badge on">
                <span className="sq" />
                Fiscal NFe
              </span>
              <span className="badge">
                <span className="sq" />
                Cobrança auto
              </span>
              <span className="badge">
                <span className="sq" />
                SPED
              </span>
            </div>
          </div>
        </div>
        <div className="snapshot-frame">
          <div className="chrome">
            <div className="dots">
              <span />
              <span />
              <span />
            </div>
            <div className="url">app.avant.com.br / painel-de-desempenho</div>
            <span className="chrome-badge">Plano: Industry + Fiscal</span>
          </div>
          <img src={dashboardImg} alt="Painel de desempenho do Avant ERP" />
        </div>
        <div className="snapshot-callouts">
          <div className="callout-cell">
            <span className="num">01</span>
            <span className="lbl">KPIs em tempo real</span>
            <span className="desc">
              Ticket médio, faturamento, lucro estimado e produção do dia, num
              só painel.
            </span>
          </div>
          <div className="callout-cell">
            <span className="num">02</span>
            <span className="lbl">Sidebar por módulo</span>
            <span className="desc">
              A navegação só mostra o que está ativo no plano da empresa.
            </span>
          </div>
          <div className="callout-cell">
            <span className="num">03</span>
            <span className="lbl">Lote, vencimento e custo</span>
            <span className="desc">
              Alertas de estoque baixo e lotes vencendo aparecem no painel
              principal.
            </span>
          </div>
          <div className="callout-cell">
            <span className="num">04</span>
            <span className="lbl">Vendas por dia e por mês</span>
            <span className="desc">
              Tendência, ranking de categorias e comparativo com o período
              anterior.
            </span>
          </div>
        </div>
      </section>

      <section className="section" id="diferenciais">
        <div className="section-head">
          <span className="kicker">04 / Diferenciais</span>
          <h2>
            Profundo onde importa,
            <br />
            <span className="accent">leve onde não importa.</span>
          </h2>
        </div>
        <div className="benefits">
          <div className="benefit">
            <span className="num">01 / OPERACIONAL</span>
            <h3>
              Operação <span className="accent">centralizada.</span>
            </h3>
            <p>
              Vendas, estoque, financeiro, produção e fiscal vivem no mesmo
              fluxo — uma venda baixa estoque, gera contas a receber, dispara
              NFe e abastece o painel.
            </p>
          </div>
          <div className="benefit">
            <span className="num">02 / MODULAR</span>
            <h3>
              Módulos <span className="accent">sob medida.</span>
            </h3>
            <p>
              Cada empresa ativa apenas o que precisa. Rotas, sidebar, APIs e
              jobs respeitam o plano. Comece pelo essencial, evolua sem trocar
              de sistema.
            </p>
          </div>
          <div className="benefit">
            <span className="num">03 / RASTREÁVEL</span>
            <h3>
              Controle <span className="accent">rastreável.</span>
            </h3>
            <p>
              Lote, movimento, produção, fiscal e auditoria com histórico. Você
              sabe quem mexeu, quando, e em qual módulo — sem caixa-preta.
            </p>
          </div>
        </div>
      </section>

      <section className="section" id="modulos">
        <div className="section-head">
          <span className="kicker">05 / Módulos</span>
          <h2>
            Seis grupos.
            <br />
            <span className="accent">Onze módulos.</span> O seu plano.
          </h2>
        </div>
        <div className="modules-grouped">
          {MODGROUPS.map((g, i) => (
            <article key={i} className="modgroup">
              <div className="modgroup-head">
                <span className="modgroup-code">{g.code}</span>
                <span className="modgroup-tier">{g.tier}</span>
              </div>
              <h3 className="modgroup-title">{g.title}</h3>
              <p className="modgroup-pitch">{g.pitch}</p>
              <ul>
                {g.items.map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section pricing" id="precos">
        <div className="section-head">
          <span className="kicker">06 / Preços</span>
          <h2>
            Três planos.
            <br />
            <span className="accent">Dois add-ons.</span> Você monta o seu.
          </h2>
          <p className="section-lede">
            10 dias grátis em qualquer plano pago. Cobramos no dia 11. Cancele
            quando quiser, sem multa nem fidelidade.
          </p>
        </div>

        <div className="pricing-grid">
          {PLANS.map((p) => (
            <article
              key={p.key}
              className={`plan-card ${p.recommended ? "recommended" : ""} ${
                p.contact ? "contact" : ""
              }`}
            >
              {p.recommended && (
                <span className="plan-ribbon">Recomendado</span>
              )}
              <div className="plan-head">
                <span className="plan-code">{p.code}</span>
                <h3 className="plan-name">{p.name}</h3>
              </div>
              <p className="plan-tagline">{p.tagline}</p>

              <div className="plan-price">
                <span className="plan-price-amount">{p.monthly}</span>
                <span className="plan-price-period">/ mês</span>
              </div>
              <span className="plan-price-yearly">{p.yearly} no anual</span>

              <ul className="plan-bullets">
                {p.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>

              <button
                type="button"
                className={`btn ${p.recommended ? "primary" : "ghost"} plan-cta`}
                onClick={goSignup}
              >
                {p.cta} <span className="btn-arrow">→</span>
              </button>
            </article>
          ))}
        </div>

        <div className="addon-strip">
          <div className="addon-strip-head">
            <span className="kicker">Add-ons</span>
            <p>
              Empilhe módulos específicos em qualquer plano. Sem mudar de tier.
            </p>
          </div>
          <div className="addon-strip-grid">
            {PLAN_ADDONS.map((a) => (
              <article key={a.key} className="addon-card">
                <div className="addon-card-head">
                  <span className="addon-code">{a.code}</span>
                  <h4 className="addon-name">{a.name}</h4>
                </div>
                <p className="addon-pitch">{a.pitch}</p>
                <div className="addon-foot">
                  <span className="addon-price">{a.price}</span>
                  <span className="addon-period">/ mês</span>
                  <span className="addon-yearly">· {a.yearly}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="deep" id="industria">
        <div className="copy">
          <div className="kicker">
            <span className="sq" />
            Add-on / Indústria
          </div>
          <h2>
            Fórmula, estoque
            <br /> e custo <span className="accent">conectados.</span>
          </h2>
          <p className="lede">
            O módulo de Indústria transforma{" "}
            <strong>formulações em lotes de produção</strong>, consome
            matéria-prima por FEFO, gera lotes de saída com custo calculado e
            registra desvios — tudo dentro do mesmo sistema que vende.
          </p>
          <ul>
            <li>
              <span className="num">F.01</span>
              <div>
                <strong>BOM multinível.</strong>{" "}
                <span className="note">
                  Fichas técnicas com sub-receitas, versões e histórico de
                  custo.
                </span>
              </div>
            </li>
            <li>
              <span className="num">F.02</span>
              <div>
                <strong>Consumo por FEFO.</strong>{" "}
                <span className="note">
                  Matéria-prima sai do lote mais próximo do vencimento,
                  automático.
                </span>
              </div>
            </li>
            <li>
              <span className="num">F.03</span>
              <div>
                <strong>Lote de saída.</strong>{" "}
                <span className="note">
                  Cada OP gera um lote com custo unitário, validade e rastreio.
                </span>
              </div>
            </li>
            <li>
              <span className="num">F.04</span>
              <div>
                <strong>Desvio & cancelamento.</strong>{" "}
                <span className="note">
                  Registro de perda, retrabalho e estorno — com motivo e autor.
                </span>
              </div>
            </li>
            <li>
              <span className="num">F.05</span>
              <div>
                <strong>Relatórios.</strong>{" "}
                <span className="note">
                  Volume produzido, consumo real vs. teórico, custo por OP.
                </span>
              </div>
            </li>
          </ul>
          <div className="cta-inline">
            <button type="button" className="btn primary" onClick={goSignup}>
              Adicionar add-on Indústria <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
        <div className="visual">
          <span className="vlabel">{"// product · formulação"}</span>
          <span className="corner-tag">
            <span className="sq" />
            Módulo ativo
          </span>
          <img src={formulacaoImg} alt="Tela de formulação do módulo Indústria" />
        </div>
      </section>

      <section className="deep flip" id="fiscal">
        <div className="visual">
          <span className="vlabel">{"// product · estoque por lote"}</span>
          <span className="corner-tag">
            <span className="sq" />
            Lote · validade · custo
          </span>
          <img src={estoqueImg} alt="Tela de estoque por lote do Avant ERP" />
        </div>
        <div className="copy">
          <div className="kicker">
            <span className="sq" />
            07 / Fiscal
          </div>
          <h2>
            Fiscal dentro da
            <br /> <span className="accent">operação.</span>
          </h2>
          <p className="lede">
            Configure certificado A1{" "}
            <strong>criptografado em repouso</strong>, série, regime e naturezas
            no mesmo ambiente que vende e controla estoque. CCe, devolução e
            contingência automática já vêm no pacote.
          </p>
          <ul>
            <li>
              <span className="num">N.01</span>
              <div>
                <strong>Configuração por empresa.</strong>{" "}
                <span className="note">
                  Certificado A1 (AES-256), série, regime tributário, naturezas
                  e CFOPs.
                </span>
              </div>
            </li>
            <li>
              <span className="num">N.02</span>
              <div>
                <strong>NFe, CCe & devolução.</strong>{" "}
                <span className="note">
                  Emissão, carta de correção, nota de devolução e cancelamento
                  na janela de 24h — tudo no mesmo fluxo.
                </span>
              </div>
            </li>
            <li>
              <span className="num">N.03</span>
              <div>
                <strong>DANFE em PDF.</strong>{" "}
                <span className="note">
                  Renderização server-side a partir do XML autorizado, pronta
                  pra imprimir ou anexar.
                </span>
              </div>
            </li>
            <li>
              <span className="num">N.04</span>
              <div>
                <strong>Contingência automática.</strong>{" "}
                <span className="note">
                  Após falhas seguidas de transmissão, troca pra SVC sem
                  operador — caixa não para.
                </span>
              </div>
            </li>
            <li>
              <span className="num">N.05</span>
              <div>
                <strong>SPED · Sintegra.</strong>{" "}
                <span className="note">
                  Disponível quando o módulo fiscal avançado estiver ativo no
                  plano.
                </span>
              </div>
            </li>
          </ul>
          <div className="cta-inline">
            <a href="#modulos" className="btn">
              Ver requisitos fiscais
            </a>
          </div>
        </div>
      </section>

      <section className="deep" id="cobranca">
        <div className="copy">
          <div className="kicker">
            <span className="sq" />
            08 / Cobrança Automática
          </div>
          <h2>
            Menos cobrança manual,
            <br /> mais <span className="accent">previsibilidade.</span>
          </h2>
          <p className="lede">
            Com o módulo de cobrança automática, o Avant usa regras de atraso
            pra enviar lembretes por <strong>WhatsApp</strong> aos clientes com
            cobrança ativada. A operação acompanha — não executa.
          </p>
          <ul>
            <li>
              <span className="num">C.01</span>
              <div>
                <strong>Agenda de cobrança.</strong>{" "}
                <span className="note">
                  Frequência, horário, dias mínimos em atraso, intervalo entre
                  reenvios.
                </span>
              </div>
            </li>
            <li>
              <span className="num">C.02</span>
              <div>
                <strong>Conexão WhatsApp.</strong>{" "}
                <span className="note">
                  Vincula a conta da empresa e respeita opt-in/out por cliente.
                </span>
              </div>
            </li>
            <li>
              <span className="num">C.03</span>
              <div>
                <strong>Contas a receber integradas.</strong>{" "}
                <span className="note">
                  Régua dispara a partir das parcelas em atraso reais — sem
                  planilha.
                </span>
              </div>
            </li>
            <li>
              <span className="num">C.04</span>
              <div>
                <strong>Controle por cliente.</strong>{" "}
                <span className="note">
                  Cada cliente tem o seu próprio estado de cobrança automática.
                </span>
              </div>
            </li>
          </ul>
          <div className="cta-inline">
            <button type="button" className="btn primary" onClick={goSignup}>
              Conhecer o módulo <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
        <div className="visual">
          <span className="vlabel">{"// product · agendamento"}</span>
          <span className="corner-tag">
            <span className="sq" />
            Próx. execução: 09h
          </span>
          <img src={cobrancaImg} alt="Configuração de cobrança automática" />
        </div>
      </section>

      <section className="section" id="para-quem">
        <div className="section-head">
          <span className="kicker">09 / Para Quem É</span>
          <h2>
            Quatro perfis,
            <br /> um <span className="accent">caminho de crescimento.</span>
          </h2>
        </div>
        <div className="personas">
          {PERSONAS.map((p, i) => (
            <div key={i} className="persona">
              <div className="num">{p.n}</div>
              <div className="title">{p.title}</div>
              <div className="desc">{p.desc}</div>
              <div className="stack">
                {p.stack.map((s, j) => (
                  <span
                    key={j}
                    className={"chip" + (s.endsWith("on") ? " on" : "")}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="measure">
        <div className="measure-cell">
          <span className="lbl">Módulos disponíveis</span>
          <span className="val">
            11<span className="unit" />
          </span>
        </div>
        <div className="measure-cell">
          <span className="lbl">Conformidade fiscal</span>
          <span className="val">
            NFe<span className="unit">SPED</span>
          </span>
        </div>
        <div className="measure-cell">
          <span className="lbl">Rastreio de estoque</span>
          <span className="val">
            Lote<span className="unit">FEFO</span>
          </span>
        </div>
        <div className="measure-cell">
          <span className="lbl">Instalação</span>
          <span className="val">
            0<span className="unit">arq.</span>
          </span>
        </div>
        <div className="measure-cell">
          <span className="lbl">Pra começar</span>
          <span className="val">
            R$0<span className="unit">,00</span>
          </span>
        </div>
      </div>

      <section className="section" id="funciona">
        <div className="section-head">
          <span className="kicker">10 / Como Funciona</span>
          <h2>
            Três passos <span className="accent">e roda.</span>
          </h2>
        </div>
        <div className="steps">
          {STEPS.map((s, i) => (
            <div key={i} className="step">
              <div className="step-head">
                <span className="step-num">{s.n}</span>
                <span className="step-tag">
                  <strong>{s.tagA}</strong>
                  {s.tagB}
                </span>
              </div>
              <h3>{s.h}</h3>
              <p>{s.p}</p>
              <div className="punchline">{s.punch}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="final" id="contato">
        <span className="stamp">
          <span className="sq" />
          Feito no Brasil · para empresas brasileiras
        </span>
        <h2>
          Comece simples.
          <br />
          <span className="accent">
            Ative módulos quando
            <br />
            sua operação pedir.
          </span>
        </h2>
        <p className="sub">
          O Avant ERP acompanha sua empresa por etapa: venda, estoque, produção,
          financeiro e fiscal — sem trocar de sistema no meio do caminho.
        </p>
        <div className="cta-row">
          <button type="button" className="btn primary" onClick={goSignup}>
            Começar grátis <span className="btn-arrow">→</span>
          </button>
          <a href="#modulos" className="btn">
            Ver módulos
          </a>
        </div>
      </section>

      <footer className="footer">
        <div className="left">
          <LogoMark size={20} />
          <span>Avant / ERP</span>
          <span>© {new Date().getFullYear()} · Todos os direitos reservados</span>
        </div>
        <div className="right">
          <a href="#top">Termos</a>
          <a href="#top">Privacidade</a>
          <a href="#top">Status</a>
          <a href="mailto:contato@avant.com.br">contato@avant.com.br</a>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
